import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [sessionChecked, setSessionChecked] = useState(false);
    const [hasValidSession, setHasValidSession] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if we have a valid recovery session
        const checkSession = async () => {
            if (!supabase) {
                setError('Autentificarea nu este configurată');
                setSessionChecked(true);
                return;
            }

            try {
                // Supabase automatically handles the recovery token from URL hash
                const { data: { session }, error } = await supabase.auth.getSession();
                
                if (error) {
                    console.error('Session error:', error);
                    setError('Link-ul de resetare este invalid sau a expirat');
                } else if (session) {
                    setHasValidSession(true);
                } else {
                    // Check URL hash for recovery token
                    const hashParams = new URLSearchParams(window.location.hash.substring(1));
                    const accessToken = hashParams.get('access_token');
                    const type = hashParams.get('type');
                    
                    if (accessToken && type === 'recovery') {
                        // Set the session with the recovery token
                        const { error: setSessionError } = await supabase.auth.setSession({
                            access_token: accessToken,
                            refresh_token: hashParams.get('refresh_token') || '',
                        });
                        
                        if (setSessionError) {
                            setError('Link-ul de resetare este invalid sau a expirat');
                        } else {
                            setHasValidSession(true);
                        }
                    } else {
                        setError('Link-ul de resetare este invalid sau a expirat. Te rugăm să soliciți un nou link.');
                    }
                }
            } catch (err) {
                console.error('Error checking session:', err);
                setError('A apărut o eroare. Te rugăm să încerci din nou.');
            }
            
            setSessionChecked(true);
        };

        checkSession();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Parolele nu se potrivesc');
            return;
        }

        if (password.length < 6) {
            setError('Parola trebuie să aibă cel puțin 6 caractere');
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) {
                throw error;
            }

            setSuccess(true);
            
            // Sign out after password reset
            await supabase.auth.signOut();
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            console.error('Password update error:', err);
            setError(err.message || 'A apărut o eroare la resetarea parolei');
        } finally {
            setLoading(false);
        }
    };

    // Loading state while checking session
    if (!sessionChecked) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Se verifică link-ul...</p>
                </div>
            </div>
        );
    }

    // Invalid or expired link
    if (!hasValidSession && !success) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <Card className="bg-white border-gray-200 shadow-sm text-center">
                        <CardHeader>
                            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="w-8 h-8 text-red-600" />
                            </div>
                            <CardTitle className="font-heading text-xl text-gray-900">Link invalid</CardTitle>
                            <CardDescription className="text-gray-500">
                                {error || 'Link-ul de resetare este invalid sau a expirat.'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Link to="/forgot-password">
                                <Button className="w-full bg-blue-600 hover:bg-blue-700" data-testid="request-new-link-btn">
                                    Solicită un nou link
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <Card className="bg-white border-gray-200 shadow-sm text-center">
                        <CardHeader>
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <CardTitle className="font-heading text-xl text-gray-900">Parolă resetată!</CardTitle>
                            <CardDescription className="text-gray-500">
                                Parola ta a fost schimbată cu succes. Vei fi redirecționat către pagina de login...
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Link to="/login">
                                <Button className="w-full bg-blue-600 hover:bg-blue-700" data-testid="goto-login-btn">
                                    Mergi la Login
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-2 mb-8">
                    <img 
                        src="https://blog.mersi.ai/wp-content/uploads/2025/05/mersi.ai-logo-1024px.jpg" 
                        alt="Mersi.ai Logo" 
                        className="w-10 h-10 rounded object-cover"
                    />
                    <span className="font-heading text-xl text-gray-900">VibeCoder</span>
                </Link>

                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading text-xl text-gray-900">Setează parola nouă</CardTitle>
                        <CardDescription className="text-gray-500">
                            Introdu noua ta parolă
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="reset-error">
                                    {error}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-700">Parolă nouă</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 bg-white border-gray-200 text-gray-900"
                                        required
                                        minLength={6}
                                        data-testid="reset-password-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-gray-700">Confirmă parola</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 bg-white border-gray-200 text-gray-900"
                                        required
                                        minLength={6}
                                        data-testid="reset-confirm-password-input"
                                    />
                                </div>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                disabled={loading}
                                data-testid="reset-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Se resetează...
                                    </>
                                ) : (
                                    'Resetează parola'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPassword;
